import React, { useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import {
  compressImage,
  ImageCompressionOptions,
} from 'utils/helpers/compressImage';
import './styles.scss';

type Props = {
  avatar?: string;
  variant?: 'default' | 'compact';
  label?: string;
  compression?: ImageCompressionOptions | false;
  onChangeCallback?: (file: File) => void;
};

const AvatarUploader = (props: Props) => {
  const {
    onChangeCallback,
    avatar,
    compression,
    variant = 'default',
    label = 'ảnh',
  } = props;
  const isCompact = variant === 'compact';
  const busy = useRef(false);
  const mounted = useRef(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [avatarState, setAvatarState] = useState<string>(avatar || '');

  useEffect(() => {
    setAvatarState(avatar || (isCompact ? '' : '/assets/images/no-image.jpg'));
  }, [avatar, isCompact]);

  const handleDrop = async (acceptedFiles: File[]) => {
    if (busy.current || !acceptedFiles[0]) return;
    busy.current = true;
    setProcessing(true);
    setError('');
    try {
      const file = await compressImage(acceptedFiles[0], compression);
      const preview = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () =>
          reject(new Error('Không thể hiển thị ảnh đã chọn.'));
        reader.readAsDataURL(file);
      });
      if (!mounted.current) return;
      setAvatarState(preview);
      onChangeCallback?.(file);
    } catch (cause) {
      if (mounted.current)
        setError(
          cause instanceof Error ? cause.message : 'Không thể xử lý ảnh.',
        );
    } finally {
      busy.current = false;
      if (mounted.current) setProcessing(false);
    }
  };

  return (
    <Dropzone
      onDrop={handleDrop}
      onDropRejected={() => setError('Vui lòng chọn một file ảnh hợp lệ.')}
      accept='image/*'
      multiple={false}
      disabled={processing}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div
          {...getRootProps({
            role: 'button',
            'aria-label': `Chọn hoặc thay ${label}`,
          })}
          aria-busy={processing}
          className={
            isCompact
              ? `avatar-uploader-compact${isDragActive ? ' is-drag-active' : ''}`
              : 'secondary-info__avatar-section__avatar'
          }
        >
          <input {...getInputProps()} />
          {processing && <span role='status'>Đang xử lý ảnh…</span>}
          {error && <span role='alert'>{error}</span>}
          {avatarState ? (
            <img
              src={avatarState}
              className={
                isCompact
                  ? 'avatar-uploader-compact__image'
                  : 'secondary-info__avatar-section__avatar__img'
              }
              alt={isCompact ? label : 'Avatar'}
            />
          ) : (
            <div
              className={
                isCompact
                  ? 'avatar-uploader-compact__placeholder'
                  : 'secondary-info__avatar-section__avatar__placeholder'
              }
            >
              {isCompact ? (
                <>
                  <svg
                    width='32'
                    height='32'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    aria-hidden='true'
                  >
                    <rect x='3' y='3' width='18' height='18' rx='3' />
                    <circle cx='8' cy='8' r='1.5' />
                    <path d='m3 17 6-6 4 4 3-3 5 5' />
                  </svg>
                  <span>{isDragActive ? 'Thả ảnh vào đây' : 'Chọn ảnh'}</span>
                  <small>hoặc kéo thả vào đây</small>
                </>
              ) : (
                'Drag and drop an image here, or click to select an image.'
              )}
            </div>
          )}
          {isCompact && avatarState && (
            <span className='avatar-uploader-compact__caption'>
              Nhấn để đổi ảnh
            </span>
          )}
        </div>
      )}
    </Dropzone>
  );
};

export default AvatarUploader;
